import { THEME } from 'src/services/consts/app.const'
import { TICKET_STATUS } from 'src/services/consts/ticket.const'
import { TICKET_CUSTOM_IDS } from 'src/services/consts/ticket-creation.const'
import { formatWebsiteUrl } from 'src/services/app/util.service'
import {
  idForCustomEntry,
  isTicketUnderReview,
  resolveCustomWebsites,
} from 'src/services/helpers/ticket.helper'

// Calculate account statistics from tickets and approvals
export function calculateAccountStatistics(accountData) {
  if (!accountData) {
    return {
      underReview: 0,
      deniedRequests: 0,
      approvedRequests: 0,
      totalRequests: 0,
      activeApprovals: 0,
      inactiveApprovals: 0,
      totalApprovals: 0,
    }
  }

  const { tickets = [], approvals = [] } = accountData

  // Ticket metrics (excluding drafts - OPENED status)
  const ticketMetrics = tickets.reduce(
    (acc, ticket) => {
      if (ticket.ticketStatus === TICKET_STATUS.OPENED) {
        return acc // Skip drafts
      }
      // Count both 'processing' and 'preprocess' as under review
      if (isTicketUnderReview(ticket.ticketStatus)) {
        acc.underReview++
      } else if (ticket.ticketStatus === TICKET_STATUS.CLOSED) {
        if (ticket.approved === true) {
          acc.approvedRequests++
        } else if (ticket.approved === false) {
          acc.deniedRequests++
        }
      }
      return acc
    },
    { underReview: 0, deniedRequests: 0, approvedRequests: 0 }
  )

  // Total requests = sum of all categorized requests (excluding drafts)
  ticketMetrics.totalRequests =
    ticketMetrics.underReview + ticketMetrics.approvedRequests + ticketMetrics.deniedRequests

  // Approval metrics
  const approvalMetrics = approvals.reduce(
    (acc, approval) => {
      acc.totalApprovals++
      if (approval.active === true) {
        acc.activeApprovals++
      } else if (approval.active === false) {
        acc.inactiveApprovals++
      }
      return acc
    },
    { activeApprovals: 0, inactiveApprovals: 0, totalApprovals: 0 }
  )

  return {
    ...ticketMetrics,
    ...approvalMetrics,
  }
}

// Enrich a ticket with company/product info from the companies dictionary
export function enrichTicket(ticket, companies) {
  if (!companies) return ticket

  const companyId = ticket.companyId
  const productId = ticket.productId

  let companyName = ticket.companyName
  let productName = ticket.productName
  let website = ''

  // Lookup in companies dict if not a custom entry
  if (companyId && !TICKET_CUSTOM_IDS.includes(companyId) && companies[companyId]) {
    companyName = companies[companyId].companyName || companyName
    website = formatWebsiteUrl(companies[companyId].website || '')

    if (
      productId &&
      !TICKET_CUSTOM_IDS.includes(productId) &&
      companies[companyId].listOfProducts?.[productId]
    ) {
      productName = companies[companyId].listOfProducts[productId].productName || productName
    }
  }

  if (companyId && TICKET_CUSTOM_IDS.includes(companyId)) {
    website = formatWebsiteUrl(ticket.companyWebsite || '')
  }

  return {
    ...ticket,
    companyName,
    productName,
    website,
  }
}

// Enrich an approval with company/product info from the companies dictionary
export function enrichApproval(approval, companies) {
  if (!companies) return approval

  const companyId = approval.companyId
  const productId = approval.productId

  let companyName = approval.companyName
  let productName = approval.productName
  let website = ''

  // Lookup in companies dict if not a custom entry
  if (companyId && !TICKET_CUSTOM_IDS.includes(companyId) && companies[companyId]) {
    companyName = companies[companyId].companyName || companyName
    website = formatWebsiteUrl(companies[companyId].website || '')

    if (
      productId &&
      !TICKET_CUSTOM_IDS.includes(productId) &&
      companies[companyId].listOfProducts?.[productId]
    ) {
      productName = companies[companyId].listOfProducts[productId].productName || productName
    }
  }

  const { isCustomCompany, isCustomProduct, companyWebsite, productWebsite } =
    resolveCustomWebsites(approval)

  if (isCustomCompany) {
    website = formatWebsiteUrl(companyWebsite)
  }

  return {
    ...approval,
    companyName,
    productName,
    website,
    ...(isCustomCompany ? { companyWebsite } : {}),
    ...(isCustomProduct ? { productWebsite } : {}),
  }
}

function pickKnownThemeField(data = {}) {
  const isKnownTheme = data.theme === THEME.DARK || data.theme === THEME.LIGHT
  return isKnownTheme ? { theme: data.theme } : {}
}

/**
 * Maps the settings object from account load to camelCase.
 * An unknown or missing theme omits the key, so a load does not clobber a locally chosen dark
 * theme. Absent flags map to false so gated surfaces stay hidden.
 */
export function refactorSettings(data = {}) {
  return {
    showAllowList: !!data.white_list_show_on_frontend,
    showBlockList: !!data.black_list_show_on_frontend,
    allowAdditionalEmailsAigov: !!data.allow_additional_emails_aigov,
    showContactSupport: !!data.show_contact_support,
    showOnboardingTour: !!data.show_onboarding_tour,
    ...pickKnownThemeField(data),
  }
}

/**
 * A settings save returns theme and show_onboarding_tour, and omits the account-load flags
 * (allow list, block list, contact support, extra emails).
 * Only keys present on the payload are returned, so a cache merge cannot turn those flags off.
 */
export function refactorSettingsUpdate(data = {}) {
  return {
    ...('show_onboarding_tour' in data ? { showOnboardingTour: !!data.show_onboarding_tour } : {}),
    ...pickKnownThemeField(data),
  }
}

export function refactorAccountData(data) {
  if (!data.tickets.length) data.tickets = []
  if (!data.user_auths.length) data.user_auths = []

  return {
    userId: data.user_id,
    // Store raw tickets without enrichment
    tickets: data.tickets.map((ticket) => refactorRawTicket(ticket)),
    // Store raw approvals without enrichment
    approvals: data.user_auths.map((approval) => refactorRawApproval(approval)),
    companies: refactorCompaniesObject(data.company_info_dict || {}),
    newTicketQuestions: refactorNewTicketQuestions(data.new_ticket_questions || []),
    settings: refactorSettings(data.settings || {}),
  }
}

// Normalize a ticket to the minimal account format (for mutations to use)
// Expects camelCase input (already refactored by ticket.service.js)
export function normalizeTicketForAccount(ticket) {
  return {
    ticketId: ticket.ticketId,
    companyId: ticket.companyId,
    productId: ticket.productId,
    ticketStatus: ticket.ticketStatus,
    approved: ticket.approved,
    dateCreated: ticket.dateCreated,
    dateSubmitted: ticket.dateSubmitted,
    lastUserUpdate: ticket.lastUserUpdate,
    // Store original names if provided (for custom/new entries)
    companyName: ticket.companyName || null,
    productName: ticket.productName || null,
    ...(ticket.companyWebsite ? { companyWebsite: ticket.companyWebsite } : {}),
    ...(ticket.productWebsite ? { productWebsite: ticket.productWebsite } : {}),
  }
}

// Normalize an approval to the minimal account format (for mutations to use)
// Expects camelCase input (already refactored by approval.service.js)
export function normalizeApprovalForAccount(approval) {
  return {
    approvalId: approval.approvalId,
    companyId: approval.companyId,
    productId: approval.productId,
    ticketId: approval.ticketId,
    active: approval.active,
    dateCreated: approval.dateCreated,
    companyName: approval.companyName || null,
    productName: approval.productName || null,
    ...(approval.companyWebsite ? { companyWebsite: approval.companyWebsite } : {}),
    ...(approval.productWebsite ? { productWebsite: approval.productWebsite } : {}),
    lastUserConfirmation: approval.lastUserConfirmation,
    nextUserConfirmation: approval.nextUserConfirmation,
  }
}

// Convert raw ticket from API to minimal camelCase format (no enrichment)
export function refactorRawTicket(ticket) {
  return {
    ticketId: ticket.ticket_id,
    companyId: idForCustomEntry(ticket.company_id, ticket.company_nf_info),
    productId: idForCustomEntry(ticket.product_id, ticket.product_nf_info),
    ticketStatus: ticket.ticket_status,
    approved: ticket.approved,
    dateCreated: ticket.date_created,
    dateSubmitted: ticket.date_submitted,
    lastUserUpdate: ticket.last_user_update,
    // Store original names if provided (for custom/new entries)
    companyName: ticket.company_name || ticket.company_nf_info?.company_nf_name || null,
    productName: ticket.product_name || ticket.product_nf_info?.product_nf_name || null,
    companyWebsite: ticket.company_nf_info?.company_nf_website || ticket.company_website || null,
    productWebsite: ticket.product_nf_info?.product_nf_website || ticket.product_website || null,
  }
}

// Convert raw approval from API to minimal camelCase format (no enrichment)
// This is for mini approvals from the account data endpoint
export function refactorRawApproval(approval) {
  return {
    approvalId: approval.user_auth_id,
    companyId: idForCustomEntry(approval.company_id, approval.company_nf_info),
    productId: idForCustomEntry(approval.product_id, approval.product_nf_info),
    ticketId: approval.ticket_id,
    active: approval.active,
    dateCreated: approval.created,
    companyName: approval.company_name || approval.company_nf_info?.company_nf_name || null,
    productName: approval.product_name || approval.product_nf_info?.product_nf_name || null,
    companyWebsite:
      approval.company_nf_info?.company_nf_website || approval.company_website || null,
    productWebsite:
      approval.product_nf_info?.product_nf_website || approval.product_website || null,
    lastUserConfirmation: approval.last_user_audit,
    nextUserConfirmation: approval.next_user_audit,
  }
}

export function refactorCompany(company, companyId) {
  return {
    companyId: companyId,
    companyName: company.company_name,
    website: company.website || '',
    listOfProducts: Object.keys(company.list_of_products || {}).reduce((products, productKey) => {
      products[productKey] = refactorProduct(company.list_of_products[productKey], productKey)
      return products
    }, {}),
  }
}

export function refactorProduct(productName, productId) {
  return {
    productId: productId,
    productName: productName,
  }
}

export function refactorCompaniesObject(companies) {
  const refactoredCompanies = {}

  Object.keys(companies).forEach((companyKey) => {
    refactoredCompanies[companyKey] = refactorCompany(companies[companyKey], companyKey)
  })

  return refactoredCompanies
}

export function refactorNewTicketQuestions(questions) {
  return questions
    .filter((q) => q.active)
    .map((question) => {
      return {
        placeholderTxt: question.placeholder_text || null,
        questionId: question.question_id,
        questionText: question.question,
        questionType: question.question_type || 'text',
        options: question.options || null,
        required: question.required || false,
      }
    })
}
